import Logger from "./core/internal/logger"
import { DEFAULT_BATCH_SIZE, DEFAULT_FLUSH_INTERVAL, METRIC_FLUSH_INTERVAL } from "./config"
import { DefaultEventProcessor } from "./core/internal/event/EventProcessor"
import HackleClientImpl from "./hackle/index.node"
import HackleClient from "./core/HackleClient"
import HackleCore from "./core/HackleCore"
import { HackleUserResolver } from "./hackle/user/index.node"
import { Metrics } from "./core/internal/metrics/Metrics"
import { MonitoringMetricRegistry } from "./core/internal/metrics/monitoring/MonitoringMetricRegistry"
import { Urls } from "./hackle/http/Urls"
import { Configurations } from "./hackle/configuration/index.node"
import { OptOutManager } from "./core/internal/optout/OptOutManager"
import { CompositeSynchronizer } from "./core/internal/sync/CompositeSynchronizer"
import { PollingSynchronizer } from "./core/internal/sync/PollingSynchronizer"
import { TimerScheduler } from "./core/internal/scheduler/TimerScheduler"
import { HttpWorkspaceFetcher } from "./core/internal/workspace/HttpWorkspaceFetcher"
import { WorkspaceManager } from "./core/internal/workspace/WorkspaceManager"
import { NodeEventDispatcher } from "./hackle/event/dispatcher/index.node"
import { EventTransport } from "./core/internal/event/EventTransport"
import { Transports } from "./core/internal/transport/Transports"
import { HttpTransport } from "./core/internal/transport/HttpTransport"
import { NoopWorkspaceConfigStorage } from "./core/internal/workspace/WorkspaceConfig"
import UserEventFactory from "./core/internal/event/UserEventFactory"
import { SystemClock } from "./core/internal/util/TimeUtil"
import { HackleClientHolder } from "./core/HackleClientHolder"

const log = Logger.log

const hackleClientHolder = new HackleClientHolder<HackleClient>()

interface Config {
  debug?: boolean
  log_disabled?: boolean
  pollingIntervalMillis?: number
  region?: string

  [key: string]: any
}

function createInstance(sdkKey: string, _config?: Config): HackleClient {
  // Config
  const config = Configurations.config(_config)

  // Logging
  Configurations.logging(config)

  // SDK
  const sdk = Configurations.sdk(sdkKey)

  // check cache
  const clientCache = hackleClientHolder.getOrNull()
  if (clientCache) {
    log.debug("use already exists hackleClient")
    return clientCache
  }

  // Transport
  const transport = Transports.intercept(sdk, HttpTransport.create())

  // Synchronizer
  const compositeSynchronizer = new CompositeSynchronizer()
  const pollingSynchronizer = new PollingSynchronizer(
    compositeSynchronizer,
    new TimerScheduler("setInterval"),
    Configurations.pollingIntervalMillis(config)
  )

  // WorkspaceManager
  const workspaceConfigStorage = new NoopWorkspaceConfigStorage()
  const httpWorkspaceFetcher = new HttpWorkspaceFetcher(Urls.serverFetch(config.sdkUrl, sdkKey), transport)
  const workspaceManager = new WorkspaceManager(workspaceConfigStorage, httpWorkspaceFetcher)
  compositeSynchronizer.add(workspaceManager)

  // EventProcessor
  const eventTransport = new EventTransport(Urls.serverDispatch(config.eventUrl), transport)
  const eventDispatcher = new NodeEventDispatcher(eventTransport)
  const eventProcessor = new DefaultEventProcessor(
    eventDispatcher,
    DEFAULT_BATCH_SIZE,
    new TimerScheduler("setInterval"),
    DEFAULT_FLUSH_INTERVAL,
    new OptOutManager(false)
  )

  // Metrics
  if (config.enableMonitoring) {
    const monitoringMetricRegistry = new MonitoringMetricRegistry(
      Urls.monitoring(config.monitoringUrl),
      transport,
      transport,
      new TimerScheduler("setInterval"),
      METRIC_FLUSH_INTERVAL
    )
    Metrics.addRegistry(monitoringMetricRegistry)
  }

  const userEventFactory = new UserEventFactory(SystemClock.instance)

  // Core
  const core = HackleCore.create(workspaceManager, userEventFactory, eventProcessor, [])
  const hackleUserResolver = new HackleUserResolver()

  // Instantiate
  pollingSynchronizer.start()
  const hackleClient = new HackleClientImpl(core, pollingSynchronizer, hackleUserResolver)
  hackleClientHolder.set(hackleClient)
  return hackleClient
}

export { PropertyOperationsBuilder, PropertyOperations } from "./hackle/property/PropertyOperations"
export { createInstance }
export { HackleClient }
export { Logger }
export { Config }
export { EmptyParameterConfig, ParameterConfig } from "./core/internal/config/ParameterConfig"
export {
  HackleSubscriptionOperationsBuilder,
  HackleSubscriptionOperations
} from "./hackle/subscription/HackleSubscriptionOperations"
export { HackleSubscriptionStatus } from "./hackle/subscription/HackleSubscriptionStatus"

export * from "./core/internal/model/model"
export * from "./public/model"

export default {
  createInstance,
  Logger
}
